import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';

export interface PaymentData {
  reservation: string | null;
  patient: string;
  type: 'deposit' | 'final' | 'refund' | 'adjustment'; // Tipos según backend Django
  method: 'card' | 'transfer' | 'cash'; // Métodos según backend Django
  status: 'pending' | 'authorized' | 'captured' | 'verified' | 'failed' | 'refunded' | 'canceled'; // Estados según backend Django
  amount: string;
  currency: 'MXN' | 'USD';
  external_ref?: string;
  fx_rate?: string;
  amount_mxn?: string;
}

export interface PaymentHistory {
  payment_id: string;
  amount: string;
  type: string;
  method: string;
  status: string;
  created_at: string;
}

@Component({
  selector: 'lib-payment-registration-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    Dialog,
    Select,
    InputText,
    InputNumber,
    Button,
    Card,
  ],
  templateUrl: './payment-registration-modal.component.html',
  styleUrls: ['./payment-registration-modal.component.css'],
})
export class PaymentRegistrationModalComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);

  @Input() visible = false;
  @Input() reservationId: string | null = null;
  @Input() patientId: string | null = null;
  @Input() totalAmount = 0;
  @Input() paidAmount = 0;
  @Input() paymentHistory: PaymentHistory[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() paymentSubmit = new EventEmitter<PaymentData>();
  @Output() paymentStatusChange = new EventEmitter<PaymentHistory>();

  paymentForm!: FormGroup;

  paymentTypes = [
    { labelKey: 'reservations.payments.types.deposit', value: 'deposit' },
    { labelKey: 'reservations.payments.types.final', value: 'final' },
    { labelKey: 'reservations.payments.types.refund', value: 'refund' },
    { labelKey: 'reservations.payments.types.adjustment', value: 'adjustment' },
  ];

  paymentMethods = [
    { labelKey: 'reservations.payments.methods.card', value: 'card' },
    { labelKey: 'reservations.payments.methods.transfer', value: 'transfer' },
    { labelKey: 'reservations.payments.methods.cash', value: 'cash' },
  ];

  paymentStatuses = [
    { labelKey: 'reservations.payments.status.pending', value: 'pending' },
    { labelKey: 'reservations.payments.status.authorized', value: 'authorized' },
    { labelKey: 'reservations.payments.status.captured', value: 'captured' },
    { labelKey: 'reservations.payments.status.verified', value: 'verified' },
    { labelKey: 'reservations.payments.status.failed', value: 'failed' },
    { labelKey: 'reservations.payments.status.refunded', value: 'refunded' },
    { labelKey: 'reservations.payments.status.canceled', value: 'canceled' },
  ];

  currencies = [
    { label: 'MXN', value: 'MXN' },
    { label: 'USD', value: 'USD' },
  ];

  get remainingBalance(): number {
    return this.totalAmount - this.paidAmount;
  }

  get balanceAfterPayment(): number {
    const paymentAmount = this.paymentForm.get('amount')?.value || 0;
    return this.remainingBalance - parseFloat(paymentAmount);
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Si el modal se abre, resetear el formulario
    if (changes['visible'] && this.visible && this.paymentForm) {
      console.log('💳 Modal abierto - Reseteando formulario');
      console.log('   Total Amount:', this.totalAmount);
      console.log('   Paid Amount:', this.paidAmount);
      console.log('   Remaining Balance:', this.remainingBalance);
      this.resetForm();
    }

    // Si cambian los montos mientras el modal está abierto, recalcular
    if (
      this.visible &&
      (changes['totalAmount'] || changes['paidAmount'] || changes['paymentHistory'])
    ) {
      console.log('💰 Valores actualizados en el modal:');
      console.log('   Total Amount:', this.totalAmount);
      console.log('   Paid Amount:', this.paidAmount);
      console.log('   Remaining Balance:', this.remainingBalance);
    }
  }

  initializeForm(): void {
    this.paymentForm = this.fb.group({
      type: ['deposit', Validators.required],
      method: ['card', Validators.required],
      status: ['pending', Validators.required],
      amount: ['', [Validators.required, Validators.min(0)]],
      currency: ['USD', Validators.required],
      external_ref: [''],
      fx_rate: ['18.60'],
      amount_mxn: [{ value: '', disabled: true }],
    });

    // Calcular amount_mxn automáticamente
    this.paymentForm.get('amount')?.valueChanges.subscribe(() => {
      this.calculateAmountMxn();
    });

    this.paymentForm.get('fx_rate')?.valueChanges.subscribe(() => {
      this.calculateAmountMxn();
    });

    this.paymentForm.get('currency')?.valueChanges.subscribe(() => {
      this.calculateAmountMxn();
    });
  }

  calculateAmountMxn(): void {
    const amount = this.paymentForm.get('amount')?.value;
    const fxRate = this.paymentForm.get('fx_rate')?.value;
    const currency = this.paymentForm.get('currency')?.value;

    if (amount && fxRate && currency === 'USD') {
      const amountMxn = parseFloat(amount) * parseFloat(fxRate);
      this.paymentForm.patchValue(
        {
          amount_mxn: amountMxn.toFixed(2),
        },
        { emitEvent: false },
      );
    } else if (currency === 'MXN') {
      this.paymentForm.patchValue(
        {
          amount_mxn: amount || '',
        },
        { emitEvent: false },
      );
    }
  }

  resetForm(): void {
    this.paymentForm.reset({
      type: 'deposit',
      method: 'card',
      status: 'pending',
      currency: 'USD',
      fx_rate: '18.60',
    });
  }

  onSubmit(): void {
    if (this.paymentForm.invalid) {
      Object.keys(this.paymentForm.controls).forEach((key) => {
        this.paymentForm.get(key)?.markAsTouched();
      });
      return;
    }

    // Solo requerir patientId - reservationId puede ser null para pagos temporales
    if (!this.patientId) {
      console.error('❌ Falta patientId');
      return;
    }

    const formValue = this.paymentForm.getRawValue();

    const paymentData: PaymentData = {
      reservation: this.reservationId, // Puede ser null para pagos temporales
      patient: this.patientId,
      type: formValue.type,
      method: formValue.method,
      status: formValue.status,
      amount: String(formValue.amount),
      currency: formValue.currency,
      external_ref: formValue.external_ref || '',
      fx_rate: formValue.fx_rate || '1.00',
      amount_mxn: formValue.amount_mxn || String(formValue.amount),
    };

    console.log('💳 Datos del pago a emitir:', paymentData);
    console.log(
      '📋 Modo:',
      this.reservationId ? 'Pago directo (con reserva)' : 'Pago temporal (sin reserva)',
    );
    this.paymentSubmit.emit(paymentData);
  }

  onCancel(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
  }

  onHide(): void {
    console.log('🚪 Modal cerrado - Limpiando formulario');
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
  }

  onPaymentStatusChange(payment: PaymentHistory): void {
    console.log('📝 Cambiando estado del pago:', payment.payment_id, 'a', payment.status);
    // Emitir el pago completo para que el padre tenga todos los datos
    this.paymentStatusChange.emit(payment);
  }
}
